import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/seo/SEOHead";
import { 
  ArrowRight, 
  Lightbulb,
  Users,
  Award,
  Zap,
  Globe,
  Brain,
  Play,
  Shield,
  Handshake,
  Search,
  BarChart3,
  AlertTriangle,
  ShoppingCart,
  ClipboardCheck,
  Linkedin,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ParticleField, MorphBlob, NodeNetwork, FlowLines, DataStream } from "@/components/effects/DynamicBackgrounds";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

/* Animated counter hook */
const useCounter = (end, duration = 2000) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, end, duration]);

  return { count, ref };
};

const AnimatedStat = ({ value, suffix = "", label }) => {
  const { count, ref } = useCounter(value);
  return (
    <div ref={ref} className="text-center">
      <div className="text-5xl font-bold text-[#00B4D8] mb-1">
        {count}{suffix}
      </div>
      <p className="text-[#94A3B8] text-sm">{label}</p>
    </div>
  );
};

const values = [
  { icon: Zap, title: "Speed", description: "You get insights in hours, not weeks. No lengthy implementations." },
  { icon: Lightbulb, title: "Intelligence", description: "AI that augments your decisions. Not replaces your team." },
  { icon: Users, title: "Accessibility", description: "Enterprise-grade without enterprise complexity. Built for organisations of all sizes." },
  { icon: Award, title: "Data Control", description: "Your data stays yours. Full sovereignty and security." }
];

const companyValues = [
  { icon: Award, title: "Excellence", text: "Rigorous standards across every engagement — from strategic sourcing to AI analytics." },
  { icon: Users, title: "Customer Centric", text: "Solutions tailored to your unique procurement challenges and organisational objectives." },
  { icon: Lightbulb, title: "Innovation", text: "Cutting-edge AI, data analytics, and strategic thinking to stay ahead of the curve." },
  { icon: Shield, title: "Integrity", text: "Honesty, ethical practices, and transparency in all aspects of our work." },
  { icon: Handshake, title: "Collaboration", text: "We act as an extension of your team — co-creating solutions that maximise value." },
  { icon: ClipboardCheck, title: "Accountability", text: "We deliver on our promises with measurable results aligned to your strategic goals." }
];

const advisoryServices = [
  { icon: BarChart3, title: "Spend Analysis", description: "Advanced spend analytics powered by AI to consolidate and categorise expenditures, providing data-driven assessment of savings opportunities." },
  { icon: Search, title: "Strategic Sourcing", description: "Identify and exploit sustainable advantages through industry best practices and bespoke solutions tailored to your business objectives." },
  { icon: ClipboardCheck, title: "Screening & Background Checks", description: "Vendor screening services to help organisations make well-informed and confident decisions when selecting prospective partners." },
  { icon: AlertTriangle, title: "Procurement Fraud Prevention", description: "Expert detection of procurement fraud schemes — spotting red flags and mitigating opportunities before they impact your organisation." },
  { icon: ShoppingCart, title: "Buying House", description: "Leveraging collective purchasing power to secure better pricing on office essentials, marketing services, and more." },
  { icon: Globe, title: "Supplier Performance Management", description: "Proactive supplier management ensuring reliable supply chains, identifying risks early, and fostering transparency." }
];

const team = [
  { name: "Sheldon Godinho", role: "Chief Commercial Officer", bio: "Experienced leader with a proven track record in driving organisational success across B2C, B2B, and B2G sectors. Skilled in strategic growth, team building, and fostering high-level partnerships.", linkedin: "https://www.linkedin.com/in/sheldongodinho/", initials: "SG" },
  { name: "Qasim Mahmood", role: "Chief People Officer", bio: "Over 23 years of experience in the staffing and recruitment industry, specialising in delivering customised talent solutions across diverse sectors.", linkedin: "https://www.linkedin.com/in/qasimmahmood/", initials: "QM" }
];

const milestones = [
  { year: "Origins", event: "Founded as a procurement consultancy — helping organisations optimise spend through hands-on advisory, strategic sourcing, and supplier management.", tag: "Advisory" },
  { year: "Growth", event: "Expanded services to include fraud prevention, buying house operations, vendor screening, and performance management across multiple sectors.", tag: "Advisory" },
  { year: "Evolution", event: "Recognised the limits of manual processes. Began building AI-powered tools — DataRefinery, SpendLens, and ConTrack — to scale procurement intelligence.", tag: "Platform" },
  { year: "Today", event: "A dual-capability firm: our AI platform delivers instant intelligence, while our advisory team provides the hands-on expertise to drive implementation and change.", tag: "Both" }
];

const tagColors = {
  Advisory: { dot: "bg-[#10B981]", text: "text-[#10B981]", border: "border-[#10B981]/30", bg: "bg-[#10B981]/5" },
  Platform: { dot: "bg-[#00B4D8]", text: "text-[#00B4D8]", border: "border-[#00B4D8]/30", bg: "bg-[#00B4D8]/5" },
  Both: { dot: "bg-[#8B5CF6]", text: "text-[#8B5CF6]", border: "border-[#8B5CF6]/30", bg: "bg-[#8B5CF6]/5" }
};

export const AboutPage = () => {
  return (
    <div className="bg-[#020617] pt-20">
      <SEOHead
        title="About"
        description="ProcureAide combines decades of procurement advisory expertise with cutting-edge AI to deliver a new standard in procurement intelligence. Serving organisations in Pakistan and Canada."
        path="/about"
      />
      {/* Hero */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-animated opacity-30" />
        <ParticleField count={15} className="opacity-50" />
        <MorphBlob color="rgba(0,180,216,0.05)" size="500px" className="top-[10%] right-[-10%]" />
        <MorphBlob color="rgba(10,36,99,0.08)" size="400px" className="bottom-[5%] left-[-8%]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.p 
                variants={fadeInUp}
                className="text-xs uppercase tracking-[0.2em] text-[#00B4D8] mb-4"
              >
                About Procureaide
              </motion.p>
              <motion.h1 
                variants={fadeInUp}
                className="text-4xl sm:text-5xl font-bold text-[#F8FAFC] leading-tight tracking-tight mb-6"
              >
                Restructuring Procurement with{" "}
                <span className="text-[#00B4D8]">Intelligence</span>
              </motion.h1>
              <motion.p 
                variants={fadeInUp}
                className="text-lg text-[#94A3B8] mb-6"
              >
                At Procureaide, we specialise in technology-driven procurement services and 
                strategic sourcing that transform the way organisations manage their spend.
              </motion.p>
              <motion.p 
                variants={fadeInUp}
                className="text-[#94A3B8] mb-8"
              >
                What sets us apart is our approach. We don't just advise from the outside — we 
                integrate with your team, acting as an extension of your workforce. This collaborative 
                approach, now powered by our AI platform, saves you valuable time and resources while 
                maximising efficiency.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex gap-4">
                <Link to="/#demo">
                  <Button 
                    size="lg"
                    className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4] hover:scale-105 transition-all shadow-[0_0_20px_rgba(0,180,216,0.3)]"
                    data-testid="about-try-demo-btn"
                  >
                    <Play className="mr-2 w-5 h-5" />
                    See the Platform
                  </Button>
                </Link>
                <a href="mailto:info@procureaide.com">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8]/10"
                    data-testid="about-contact-btn"
                  >
                    Get in Touch
                  </Button>
                </a>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="aspect-square max-w-md mx-auto relative">
                {/* Outer orbiting ring */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 rounded-full border border-dashed border-[#00B4D8]/15"
                />
                {/* Middle orbiting ring - reverse */}
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-6 rounded-full border border-[#10B981]/10"
                >
                  <div className="absolute -top-1.5 left-1/2 w-3 h-3 rounded-full bg-[#10B981]/60 shadow-[0_0_10px_rgba(16,185,129,0.4)]" />
                </motion.div>
                {/* Inner pulse */}
                <div className="absolute inset-12 rounded-full bg-gradient-to-br from-[#0A2463]/40 to-[#00B4D8]/20 animate-pulse" />
                {/* Core */}
                <div className="absolute inset-20 bg-[#0B1426] rounded-full flex items-center justify-center border border-[#00B4D8]/10">
                  <div className="text-center">
                    <Brain className="w-14 h-14 text-[#00B4D8] mx-auto mb-3" />
                    <p className="text-[#F8FAFC] font-bold text-lg">AI-Powered</p>
                    <p className="text-[#94A3B8] text-xs">Procurement Platform</p>
                  </div>
                </div>
                {/* Floating accent dots */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0"
                >
                  <div className="absolute top-4 right-1/4 w-2 h-2 rounded-full bg-[#00B4D8]/80 shadow-[0_0_8px_rgba(0,180,216,0.5)]" />
                </motion.div>
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0"
                >
                  <div className="absolute bottom-8 left-1/4 w-1.5 h-1.5 rounded-full bg-[#8B5CF6]/80 shadow-[0_0_8px_rgba(139,92,246,0.4)]" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-[#0B1426]/50 relative overflow-hidden">
        <NodeNetwork count={8} className="opacity-15" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-6"
            >
              Our Mission
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-[#94A3B8] leading-relaxed"
            >
              To empower businesses by optimising their procurement processes, mitigating risks, 
              and driving value creation through expert consultancy, cutting-edge technology, 
              and proven procurement strategies.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Platform Values */}
      <section className="py-20 relative overflow-hidden">
        <FlowLines count={3} className="opacity-15" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              What We Stand For
            </motion.h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="text-center group"
              >
                <div className="w-14 h-14 mx-auto rounded-xl bg-[#00B4D8]/10 flex items-center justify-center mb-4 group-hover:bg-[#00B4D8]/20 group-hover:shadow-[0_0_20px_rgba(0,180,216,0.15)] transition-all">
                  <value.icon className="w-7 h-7 text-[#00B4D8]" />
                </div>
                <h3 className="text-lg font-semibold text-[#F8FAFC] mb-2">{value.title}</h3>
                <p className="text-sm text-[#94A3B8]">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Vision - Founder */}
      <section className="py-20 bg-[#0B1426]/50 relative overflow-hidden">
        <MorphBlob color="rgba(0,180,216,0.04)" size="350px" className="top-[5%] right-[-5%]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              The Vision
            </motion.h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-[#111F36] border border-[#00B4D8]/20 rounded-2xl p-8 shadow-[0_0_40px_rgba(0,180,216,0.06)]"
            data-testid="founder-card"
          >
            <blockquote className="text-lg text-[#F8FAFC] leading-relaxed mb-6 italic border-l-2 border-[#00B4D8] pl-4">
              "Empowering Your Procurement Potential isn't just a tagline — it is the inspiration 
              that drives Procureaide to transform the procurement landscape by providing tailored, 
              innovative, and impactful solutions that unlock business growth and sustainability."
            </blockquote>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00B4D8] to-[#0A2463] flex items-center justify-center flex-shrink-0">
                <span className="text-[#F8FAFC] font-bold text-lg">AG</span>
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <p className="text-[#F8FAFC] font-semibold text-lg">Andre Godinho</p>
                  <span className="text-xs px-3 py-1 rounded-full bg-[#00B4D8]/10 text-[#00B4D8] border border-[#00B4D8]/20">
                    Founder & CEO
                  </span>
                  <a 
                    href="https://www.linkedin.com/in/andre-godinho-51456316/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#94A3B8] hover:text-[#00B4D8] transition-colors"
                    data-testid="founder-linkedin"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                </div>
                <p className="text-sm text-[#94A3B8] leading-relaxed">
                  Seasoned procurement and supply chain leader with over 20 years of expertise across 
                  telecommunications, education, and manufacturing. As Procurement Director for Asia Pacific 
                  at the British Council, Andre drove strategic sourcing, cost-saving efficiencies, and 
                  supplier performance. His vision: combine deep domain expertise with AI to make 
                  enterprise-grade procurement intelligence accessible to all.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Journey - The Progression Story */}
      <section className="py-20 relative overflow-hidden">
        <DataStream count={4} className="opacity-15" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-6"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Our Journey
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] max-w-2xl mx-auto"
            >
              From traditional procurement advisory to AI-powered intelligence — 
              built to stay relevant in a rapidly evolving market.
            </motion.p>
          </motion.div>

          <div className="relative mt-16">
            {/* Gradient timeline line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 transform md:-translate-x-1/2 overflow-hidden">
              <motion.div 
                initial={{ height: "0%" }}
                whileInView={{ height: "100%" }}
                viewport={{ once: true }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="w-full bg-gradient-to-b from-[#10B981] via-[#00B4D8] to-[#8B5CF6]"
              />
            </div>
            
            {milestones.map((milestone, index) => {
              const colors = tagColors[milestone.tag];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -40 : 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className={`relative flex items-center mb-10 ${
                    index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  {/* Timeline dot with pulse */}
                  <div className="absolute left-4 md:left-1/2 transform -translate-x-1/2 z-10">
                    <div className={`w-4 h-4 rounded-full ${colors.dot} shadow-[0_0_12px_rgba(0,180,216,0.5)]`} />
                    <div className={`absolute inset-0 w-4 h-4 rounded-full ${colors.dot} animate-ping opacity-30`} />
                  </div>
                  
                  <div className={`ml-12 md:ml-0 md:w-1/2 ${index % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12"}`}>
                    <motion.div 
                      whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                      className="bg-[#111F36] rounded-xl p-6 border border-[#00B4D8]/15 hover:border-[#00B4D8]/30 transition-all shadow-[0_0_20px_rgba(0,0,0,0.2)]"
                    >
                      <div className={`flex items-center gap-3 mb-2 ${index % 2 === 0 ? "md:justify-end" : ""}`}>
                        <span className={`font-bold text-lg ${colors.text}`}>{milestone.year}</span>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${colors.text} ${colors.border} ${colors.bg}`}>
                          {milestone.tag}
                        </span>
                      </div>
                      <p className="text-[#94A3B8]">{milestone.event}</p>
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Visual Bridge: Traditional → AI */}
      <section className="py-10 relative overflow-hidden">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="flex items-center gap-4"
          >
            <div className="flex items-center gap-2">
              <Handshake className="w-5 h-5 text-[#10B981] flex-shrink-0" />
              <span className="text-xs text-[#10B981] uppercase tracking-wider whitespace-nowrap">Advisory</span>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-[#10B981] via-[#00B4D8] to-[#8B5CF6]" />
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#8B5CF6] uppercase tracking-wider whitespace-nowrap">Platform + Advisory</span>
              <Brain className="w-5 h-5 text-[#8B5CF6] flex-shrink-0" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Advisory & Implementation Services */}
      <section className="py-20 bg-[#0B1426]/50 relative overflow-hidden" data-testid="advisory-services-section">
        <ParticleField count={10} className="opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-6"
          >
            <motion.div 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 mb-6"
            >
              <Handshake className="w-4 h-4 text-[#10B981]" />
              <span className="text-sm text-[#10B981]">Beyond the Platform</span>
            </motion.div>
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Advisory & Implementation Services
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] max-w-3xl mx-auto mb-3"
            >
              Our roots are in hands-on procurement. Our team of seasoned professionals 
              offers comprehensive advisory services — acting as an extension of your 
              workforce to drive results.
            </motion.p>
            <motion.p 
              variants={fadeInUp}
              className="text-sm text-[#64748B] max-w-2xl mx-auto"
            >
              These services can be engaged independently or combined with our platform modules 
              for maximum impact.
            </motion.p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {advisoryServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="bg-[#111F36] border border-[#10B981]/10 rounded-xl p-6 hover:border-[#10B981]/30 hover:shadow-[0_0_25px_rgba(16,185,129,0.08)] transition-all"
                data-testid={`advisory-service-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#10B981]/10 flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-[#10B981]" />
                </div>
                <h3 className="text-lg font-semibold text-[#F8FAFC] mb-2">{service.title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mt-10"
          >
            <a href="mailto:info@procureaide.com">
              <Button 
                size="lg"
                className="bg-[#10B981] text-[#020617] font-bold hover:bg-[#059669] hover:scale-105 transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)]"
                data-testid="advisory-contact-btn"
              >
                Discuss Your Needs
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 relative overflow-hidden">
        <FlowLines count={3} className="opacity-15" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Our Values
            </motion.h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {companyValues.map((val, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="bg-[#111F36] border border-[#00B4D8]/10 rounded-xl p-6 hover:border-[#00B4D8]/30 hover:shadow-[0_0_25px_rgba(0,180,216,0.06)] transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-[#00B4D8]/10 flex items-center justify-center mb-4">
                  <val.icon className="w-6 h-6 text-[#00B4D8]" />
                </div>
                <h3 className="text-lg font-semibold text-[#F8FAFC] mb-2">{val.title}</h3>
                <p className="text-sm text-[#94A3B8]">{val.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Team */}
      <section className="py-20 bg-[#0B1426]/50 relative overflow-hidden" data-testid="team-section">
        <NodeNetwork count={8} className="opacity-15" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Our Seasoned Professionals
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] max-w-2xl mx-auto"
            >
              Decades of procurement expertise combined with a passion for technology-driven transformation.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="bg-[#111F36] border border-[#00B4D8]/10 rounded-xl p-6 hover:border-[#00B4D8]/30 hover:shadow-[0_0_25px_rgba(0,180,216,0.06)] transition-all"
                data-testid={`team-member-${index}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#0A2463] to-[#00B4D8]/50 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#F8FAFC] font-bold">{member.initials}</span>
                  </div>
                  <div>
                    <h3 className="text-[#F8FAFC] font-semibold">{member.name}</h3>
                    <p className="text-xs text-[#00B4D8]">{member.role}</p>
                  </div>
                </div>
                <p className="text-sm text-[#94A3B8] leading-relaxed mb-4">{member.bio}</p>
                <a 
                  href={member.linkedin} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#00B4D8] transition-colors"
                >
                  <Linkedin className="w-4 h-4" /> LinkedIn
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Reach - Animated Counters */}
      <section className="py-20 relative overflow-hidden">
        <DataStream count={4} className="opacity-15" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div 
              variants={fadeInUp}
              className="w-20 h-20 mx-auto rounded-full bg-[#00B4D8]/10 flex items-center justify-center mb-6"
            >
              <Globe className="w-10 h-10 text-[#00B4D8]" />
            </motion.div>
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Global Intelligence
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] mb-12"
            >
              Our platform and advisory reach extends across borders.
            </motion.p>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <AnimatedStat value={40} suffix="+" label="Jurisdictions" />
            <AnimatedStat value={150} suffix="+" label="Currencies" />
            <AnimatedStat value={20} suffix="+" label="Years of Expertise" />
            <AnimatedStat value={24} suffix="/7" label="AI Processing" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0B1426]/50 relative overflow-hidden">
        <MorphBlob color="rgba(0,180,216,0.04)" size="400px" className="top-[-10%] left-[30%]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Ready to Start?
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] mb-8"
            >
              Whether you need our AI platform, advisory services, or both — 
              let's discuss what suits your organisation best.
            </motion.p>
            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap justify-center gap-4"
            >
              <Link to="/#demo">
                <Button 
                  size="lg"
                  className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4] hover:scale-105 transition-all shadow-[0_0_20px_rgba(0,180,216,0.3)] px-8"
                  data-testid="about-try-demo-btn"
                >
                  See the Platform
                </Button>
              </Link>
              <a href="mailto:info@procureaide.com">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8]/10 px-8"
                  data-testid="about-cta-btn"
                >
                  Get in Touch
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
